import { convertMoneyToMinorUnits, formatMoney } from '@theme/money-formatting';

const CART_UPDATE_EVENTS = ['shopify:cart:lines-update', 'shopify:cart:discount-update'];

class PrecisionCartTotal extends HTMLElement {
  /** @type {Promise<unknown> | null} */
  #latestPromise = null;
  #listening = false;

  connectedCallback() {
    if (this.#listening) return;
    this.#listening = true;
    for (const eventName of CART_UPDATE_EVENTS) {
      document.addEventListener(eventName, this.#onCartUpdate);
    }
  }

  disconnectedCallback() {
    if (!this.#listening) return;
    this.#listening = false;
    for (const eventName of CART_UPDATE_EVENTS) {
      document.removeEventListener(eventName, this.#onCartUpdate);
    }
    this.#latestPromise = null;
  }

  /** @param {Event & { action?: string, promise?: Promise<any> }} event */
  #onCartUpdate = async (event) => {
    if (!event.promise || typeof event.promise.then !== 'function') return;

    const pendingPromise = event.promise;
    this.#latestPromise = pendingPromise;

    try {
      const { cart, detail } = await pendingPromise;
      if (!this.isConnected || this.#latestPromise !== pendingPromise || !cart) return;

      if (event.action === 'add' && !detail?.didError) {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            const drawer = document.querySelector('theme-drawer#cart-drawer[open]');
            if (!drawer || drawer.contains(document.activeElement)) return;
            drawer.querySelector('.theme-drawer__close-button')?.focus();
          });
        });
      }

      const count = this.querySelector('[data-cart-count]');
      const total = this.querySelector('[data-cart-total]');
      const totalAmount = cart.cost?.totalAmount;

      if (count && Number.isFinite(Number(cart.totalQuantity))) {
        count.textContent = String(cart.totalQuantity);
      }

      if (!total || !totalAmount?.currencyCode) return;

      const formattedTotal = this.#formatTotal(totalAmount.amount, totalAmount.currencyCode);
      if (formattedTotal !== null) total.textContent = formattedTotal;
    } catch (error) {
      if (error?.name !== 'AbortError') console.warn('[precision-cart-total] Cart update failed:', error);
    }
  };

  /**
   * @param {string | number} amount
   * @param {string} currency
   * @returns {string | null}
   */
  #formatTotal(amount, currency) {
    const moneyFormat = this.dataset.moneyFormat;

    if (moneyFormat) {
      try {
        const minorUnits = convertMoneyToMinorUnits(String(amount), currency);
        if (minorUnits !== null) return formatMoney(minorUnits, moneyFormat, currency);
      } catch (error) {
        console.warn('[precision-cart-total] Merchant money formatting failed; using locale fallback:', error);
      }
    }

    const numericAmount = Number(amount);
    if (!Number.isFinite(numericAmount)) return null;

    const locale = this.dataset.locale || document.documentElement.lang || undefined;
    return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(numericAmount);
  }
}

if (!customElements.get('precision-cart-total')) {
  customElements.define('precision-cart-total', PrecisionCartTotal);
}
